import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import useWishlistStore from "../store/wishlistStore";
import { useProducts } from "../features/catalog/api";
import ProductCard from "../features/catalog/ProductCard";
import SCSpinner from "../components/loaders/SCSpinner";
import ErrorState from "../components/ui/ErrorState";
import useSEO from "../hooks/useSEO";

export default function Wishlist() {
  useSEO({ title: "Wishlist", noindex: true });
  // productIds is the local Zustand copy — server-synced for a logged-in
  // customer (see hooks/useWishlistSync.js), local-only for a guest.
  // Fetching all products and filtering client-side (rather than a
  // dedicated GET /wishlist?withProducts=1) keeps this store role-agnostic:
  // it never needs to know or care whether the ids came from the server.
  const productIds = useWishlistStore((s) => s.productIds);
  const { data: allProducts, isLoading, isError, refetch } = useProducts();
  const items = allProducts?.filter((p) => productIds.includes(p.id)) || [];

  if (isError) {
    return <ErrorState message="Couldn't load your wishlist." onRetry={refetch} />;
  }

  if (!isLoading && items.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <Heart size={40} className="text-mono/15" />
        <h1 className="mt-4 font-serif text-xl text-ink">Your wishlist is empty</h1>
        <p className="mt-2 text-sm text-ink-light">Tap the heart on any product to save it here.</p>
        <Link to="/categories" className="mt-6 rounded-2xl bg-[#2b2b2d] px-8 py-3 text-xs tracking-wide text-white shadow-floating dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold">
          BROWSE PRODUCTS
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <h1 className="mb-4 font-serif text-2xl text-ink">Wishlist</h1>
      {isLoading ? (
        <div className="flex justify-center py-16">
          <SCSpinner size={56} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
